import React, { useState, useEffect, useMemo } from 'react'
import { startOfDay, endOfDay, subDays } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import AnalyticsItem from '../../components/AnalyticsItem/AnalyticsItem'
import ActivityItem from '../../components/ActivityItem/ActivityItem'
import RewardsHistoryModal from '../../RewardsHistoryModal/RewardsHistoryModal'
import RewardsLoyaltyProfileModal from '../../RewardsLoyaltyProfileModal/RewardsLoyaltyProfileModal'
import RewardsAddLoyaltyActivityModal from '../../RewardsAddLoyaltyActivityModal/RewardsAddLoyaltyActivityModal'
import RewardsClientActivityDetailsModal from '../../RewardsClientActivityDetailsModal/RewardsClientActivityDetailsModal'
import {
  fetchRewardsAnalytics,
  selectRewardsAnalytics,
} from '../../../../../reducers/rewardsReducer'
import {
  fetchLoyaltyAnalytics,
  selectLoyaltyAnalytics,
} from '../../../../../reducers/loyaltyReducer'
import {
  selectBuildingsForFilter,
  selectActiveBuilding,
} from '../../../../../reducers/buildingSelectorReducer'
import { selectUserRole } from '../../../../../reducers/userDataReducer'
import { getGiftCardsAnalyticsMapping, getLoyaltyAnalyticsMapping } from '../../helpers'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import Icon from '../../../../Icons'
import Upselling from '../../../../Upselling/Upselling'

import LeaderBoardTable from './LeaderBoardTable'

const DEFAULT_DAYS_RANGE = 30
const INITIAL_DATE_FILTER = [
  subDays(startOfDay(Date.now()), DEFAULT_DAYS_RANGE),
  startOfDay(Date.now()),
]

const RewardsDashboard = ({
  userRole,
  rewardsAnalytics,
  fetchRewardsAnalytics,
  loyaltyAnalytics,
  fetchLoyaltyAnalytics,
  buildings,
  buildingData,
}) => {
  const { t } = useTranslation(['rewards-dashboard'])
  const [giftCardsDateFilter, setGiftCardsDateFilter] = useState(INITIAL_DATE_FILTER)
  const [loyaltyDateFilter, setLoyaltyDateFilter] = useState(INITIAL_DATE_FILTER)
  const [isLoading, setLoading] = useState(true)
  const [showGiftCards, setShowGiftCards] = useState(true)
  const [showLoyalty, setShowLoyalty] = useState(true)
  const [loyaltyProfile, setLoyaltyProfile] = useState(null)
  const [historyProfile, setHistoryProfile] = useState(null)
  const [addActivityProfile, setAddActivityProfile] = useState(null)
  const [activityDetails, setActivityDetails] = useState(null)
  const { displaySnackbar } = useSnackbar()
  const { rewardsAvailable, loyaltyAvailable } = buildingData || {}

  const { buildingId: paramBuildingId } = useParams()

  const fetchAnalyticsForRewards = async () => {
    const [startDate, endDate] = giftCardsDateFilter
    if (startDate && endDate) {
      setLoading(true)
      const startTimestamp = fromZonedTime(startDate, 'UTC').getTime()
      const endTimestamp = fromZonedTime(endOfDay(endDate), 'UTC').getTime()

      await fetchRewardsAnalytics({
        buildingId: paramBuildingId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      }).catch(({ message }) => displaySnackbar(message, true))
      setLoading(false)
    }
  }

  const fetchAnalyticsForLoyalty = async () => {
    const [startDate, endDate] = loyaltyDateFilter
    if (startDate && endDate) {
      setLoading(true)
      const startTimestamp = fromZonedTime(startDate, 'UTC').getTime()
      const endTimestamp = fromZonedTime(endOfDay(endDate), 'UTC').getTime()

      await fetchLoyaltyAnalytics({
        buildingId: paramBuildingId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      }).catch(({ message }) => displaySnackbar(message, true))
      setLoading(false)
    }
  }

  useEffect(() => {
    if (paramBuildingId) {
      fetchAnalyticsForRewards()
    }
  }, [giftCardsDateFilter, paramBuildingId])

  useEffect(() => {
    if (paramBuildingId && loyaltyAvailable) {
      fetchAnalyticsForLoyalty()
    }
  }, [loyaltyAvailable, loyaltyDateFilter, paramBuildingId])

  useEffect(() => {
    setGiftCardsDateFilter(INITIAL_DATE_FILTER)
    setLoyaltyDateFilter(INITIAL_DATE_FILTER)
  }, [paramBuildingId])

  const selectedBuilding = useMemo(
    () => buildings.find((building) => building.id === Number(paramBuildingId)),
    [buildings, paramBuildingId],
  )

  return (
    <React.Fragment>
      {rewardsAvailable && rewardsAnalytics && selectedBuilding ? (
        <div className="rewards-container">
          <div className={`rewards-dashboard ${showGiftCards ? '' : 'hidden'}`}>
            <span className="rewards-dashboard__title">
              {t('rewards-dashboard:giftCardsTitle')}
              <button
                className="button button-black-outline"
                onClick={() => setShowGiftCards(!showGiftCards)}
              >
                <Icon icon={`${showGiftCards ? 'showEye' : 'hideEye'}`} />
                {showGiftCards
                  ? t('rewards-dashboard:hideButton')
                  : t('rewards-dashboard:showButton')}
              </button>
            </span>
            <div className={`rewards-dashboard__wrapper ${isLoading ? 'is-loading' : ''}`}>
              <AnalyticsItem
                setDateFilter={setGiftCardsDateFilter}
                dateFilter={giftCardsDateFilter}
                analytics={rewardsAnalytics}
                mapping={getGiftCardsAnalyticsMapping(t)}
              />
              <ActivityItem
                activities={rewardsAnalytics.recentActions}
                timezone={selectedBuilding.timezone}
                viewAllLink={`/dashboard/building-dashboard/rewards/gift-cards/${paramBuildingId}`}
              />
            </div>
          </div>
        </div>
      ) : null}
      {loyaltyAvailable && loyaltyAnalytics && selectedBuilding ? (
        <div className="rewards-container">
          <div className={`rewards-dashboard ${showLoyalty ? '' : 'hidden'}`}>
            <span className="rewards-dashboard__title">
              {t('rewards-dashboard:loyaltyTitle')}
              <button
                className="button button-black-outline"
                onClick={() => setShowLoyalty(!showLoyalty)}
              >
                <Icon icon={`${showLoyalty ? 'showEye' : 'hideEye'}`} />
                {showLoyalty
                  ? t('rewards-dashboard:hideButton')
                  : t('rewards-dashboard:showButton')}
              </button>
            </span>
            <div className={`rewards-dashboard__wrapper ${isLoading ? 'is-loading' : ''}`}>
              <AnalyticsItem
                setDateFilter={setLoyaltyDateFilter}
                dateFilter={loyaltyDateFilter}
                analytics={loyaltyAnalytics}
                mapping={getLoyaltyAnalyticsMapping(t)}
              />
              <ActivityItem
                activities={loyaltyAnalytics.recentActions}
                timezone={selectedBuilding.timezone}
                viewAllLink={`/dashboard/building-dashboard/rewards/loyalty/${paramBuildingId}`}
              />
            </div>
          </div>
        </div>
      ) : null}
      {loyaltyAvailable && (
        <LeaderBoardTable
          building={selectedBuilding}
          onLoyaltyProfile={setLoyaltyProfile}
          onRewardsHistory={setHistoryProfile}
        />
      )}
      {userRole !== 'Administrator' && rewardsAvailable && !loyaltyAvailable && selectedBuilding ? (
        <div className="rewards-container">
          <Upselling
            params={{ type: 'loyalty' }}
            containerStyle={{ height: 'calc(100vh - 320px)' }}
          />
        </div>
      ) : userRole !== 'Administrator' &&
        !rewardsAvailable &&
        loyaltyAvailable &&
        selectedBuilding ? (
        <div className="rewards-container">
          <Upselling
            params={{ type: 'giftCards' }}
            containerStyle={{ height: 'calc(100vh - 320px)' }}
          />
        </div>
      ) : null}
      {historyProfile && (
        <RewardsHistoryModal
          building={selectedBuilding}
          client={historyProfile}
          onClose={() => setHistoryProfile(null)}
        />
      )}
      {loyaltyProfile && (
        <RewardsLoyaltyProfileModal
          building={selectedBuilding}
          client={loyaltyProfile}
          onClose={() => setLoyaltyProfile(null)}
          onAddActivity={() => setAddActivityProfile(loyaltyProfile)}
          setActivityDetails={setActivityDetails}
        />
      )}
      {addActivityProfile && (
        <RewardsAddLoyaltyActivityModal
          building={selectedBuilding}
          client={addActivityProfile}
          onClose={() => setAddActivityProfile(null)}
        />
      )}
      {activityDetails && (
        <RewardsClientActivityDetailsModal
          building={selectedBuilding}
          activity={activityDetails}
          onClose={() => setActivityDetails(null)}
        />
      )}
    </React.Fragment>
  )
}

RewardsDashboard.propTypes = {
  userRole: PropTypes.string,
  rewardsAnalytics: PropTypes.object,
  loyaltyAnalytics: PropTypes.object,
  userData: PropTypes.object,
  buildings: PropTypes.array,
  buildingData: PropTypes.object,
  fetchRewardsAnalytics: PropTypes.func,
  fetchLoyaltyAnalytics: PropTypes.func,
}

export default connect(
  (state) => ({
    userRole: selectUserRole(state),
    rewardsAnalytics: selectRewardsAnalytics(state),
    loyaltyAnalytics: selectLoyaltyAnalytics(state),
    buildings: selectBuildingsForFilter(state),
    buildingData: selectActiveBuilding(state),
  }),
  {
    fetchRewardsAnalytics,
    fetchLoyaltyAnalytics,
  },
)(RewardsDashboard)
